import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useLocation, useParams, useSearchParams } from 'react-router'

interface KnowledgeBasePanelContextValue {
  isOpen: boolean
  threadId: string | null
  /** Opens the panel. Pass a thread id to switch to it, `null` to start a
   * fresh conversation, or omit to reopen on whatever thread was last
   * active. `followUp`, when given, queues a message to auto-send once the
   * panel lands (see `pendingFollowUp`) — e.g. a "Go to page →" link
   * queuing "tell me more about this, now that I'm here". */
  openPanel: (threadId?: string | null, followUp?: string) => void
  closePanel: () => void
  setThreadId: (id: string | null) => void
  /** A message queued to auto-send as soon as the panel is ready to send
   * it. The consumer (side-panel.tsx) sends it once its thread history has
   * settled, then calls `clearPendingFollowUp()` — nothing sends it twice
   * or sends it before the thread is ready on its own. */
  pendingFollowUp: string | null
  clearPendingFollowUp: () => void
}

const KnowledgeBasePanelContext =
  createContext<KnowledgeBasePanelContextValue | null>(null)

/**
 * Single source of truth for "is the Knowledge Base panel open, and on
 * which thread" — mounted once at the org layout level (`layout.tsx`), so
 * it survives ANY in-app navigation for the lifetime of the org session,
 * not just the one link that opened it. Lives here (not under
 * `pages/[org_id]/knowledge-base/`) because `components/nav-v4`'s top-bar
 * toggle button needs it too, and components importing from pages would
 * be the wrong direction.
 *
 * Previously this lived entirely in `knowledge_base=true&thread_id=…` URL
 * search params, read fresh on every render. That broke the moment the
 * user did ANYTHING else that navigates without knowing to carry those
 * params forward — the venue switcher, a sidebar click, a breadcrumb —
 * and the panel would just vanish. This state is now the source of truth
 * for RENDERING — nothing reads the URL to decide whether the panel
 * shows — but it still WRITES itself back into the URL's search params on
 * every change (and on every navigation, so it re-lands on wherever the
 * user just went), one-way, via `replace` so it doesn't spam browser
 * history. That mirroring is what makes a reload or a copied/shared link
 * restore the panel — the URL is a reflection of this state, not a
 * dependency of it.
 */
export function KnowledgeBasePanelProvider({
  children,
}: {
  children: ReactNode
}) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [isOpen, setIsOpen] = useState(
    () => searchParams.get('knowledge_base') === 'true'
  )
  const [threadId, setThreadId] = useState<string | null>(() =>
    searchParams.get('thread_id')
  )
  const [pendingFollowUp, setPendingFollowUp] = useState<string | null>(null)

  // One-way mirror: state → URL, never the other way after the initial
  // seed above. Re-runs on every navigation (`pathname`) too, so the
  // params land on whatever page the user just moved to, not just the one
  // where the panel was opened.
  const { pathname } = useLocation()
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (isOpen) {
          next.set('knowledge_base', 'true')
          if (threadId) next.set('thread_id', threadId)
          else next.delete('thread_id')
        } else {
          next.delete('knowledge_base')
          next.delete('thread_id')
        }
        return next
      },
      { replace: true }
    )
  }, [isOpen, threadId, pathname, setSearchParams])

  // A KB thread belongs to exactly one org — switching orgs without a full
  // reload (the org switcher navigates within the same `/:org_id/*` route,
  // so this component never remounts) must not carry a stale thread from
  // the org just left. Guarded against firing on the initial mount, where
  // `org_id` hasn't "changed" from anything yet.
  const { org_id } = useParams<{ org_id: string }>()
  const prevOrgIdRef = useRef(org_id)
  useEffect(() => {
    if (prevOrgIdRef.current !== org_id) {
      prevOrgIdRef.current = org_id
      setIsOpen(false)
      setThreadId(null)
      setPendingFollowUp(null)
    }
  }, [org_id])

  const openPanel = useCallback((id?: string | null, followUp?: string) => {
    setIsOpen(true)
    if (id !== undefined) setThreadId(id)
    if (followUp) setPendingFollowUp(followUp)
  }, [])
  const closePanel = useCallback(() => setIsOpen(false), [])
  const clearPendingFollowUp = useCallback(() => setPendingFollowUp(null), [])

  const value = useMemo<KnowledgeBasePanelContextValue>(
    () => ({
      isOpen,
      threadId,
      openPanel,
      closePanel,
      setThreadId,
      pendingFollowUp,
      clearPendingFollowUp,
    }),
    [
      isOpen,
      threadId,
      openPanel,
      closePanel,
      pendingFollowUp,
      clearPendingFollowUp,
    ]
  )

  return (
    <KnowledgeBasePanelContext.Provider value={value}>
      {children}
    </KnowledgeBasePanelContext.Provider>
  )
}

export function useKnowledgeBasePanel(): KnowledgeBasePanelContextValue {
  const ctx = useContext(KnowledgeBasePanelContext)
  if (!ctx) {
    throw new Error(
      'useKnowledgeBasePanel must be used within a KnowledgeBasePanelProvider'
    )
  }
  return ctx
}
